// src/services/contact-information/ContactInformationService.js
import { createApi } from "@reduxjs/toolkit/query/react";

import { baseQueryWithReauth } from "../authorization/authService";

export const contactInformationApi = createApi({
  reducerPath: "contactInformationApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ContactInformation"],
  endpoints: (builder) => ({
    // Get all contacts for an application
    getContactsByApplication: builder.query({
      query: (applicationId) => ({
        url: `/contact_information/application/${applicationId}`,
        method: "GET",
      }),
      providesTags: (result, error, applicationId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "ContactInformation", id })),
              { type: "ContactInformation", id: `APP_${applicationId}` },
              { type: "ContactInformation", id: "LIST" },
            ]
          : [
              { type: "ContactInformation", id: `APP_${applicationId}` },
              { type: "ContactInformation", id: "LIST" },
            ],
    }),

    // Create a new contact
    createContact: builder.mutation({
      query: ({ applicationId, contactData }) => ({
        url: `/contact_information/`,
        method: "POST",
        params: { application_id: applicationId },
        body: contactData,
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: "ContactInformation", id: `APP_${applicationId}` },
        { type: "ContactInformation", id: "LIST" },
      ],
    }),

    // Update an existing contact
    updateContact: builder.mutation({
      query: ({ contactId, contactData }) => ({
        url: `/contact_information/${contactId}`,
        method: "PUT",
        body: contactData,
      }),
      invalidatesTags: (result, error, { contactId, applicationId }) => [
        { type: "ContactInformation", id: contactId },
        { type: "ContactInformation", id: `APP_${applicationId}` },
        { type: "ContactInformation", id: "LIST" },
      ],
    }),

    // Delete a contact
    deleteContact: builder.mutation({
      query: ({ contactId }) => ({
        url: `/contact_information/${contactId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { contactId, applicationId }) => [
        { type: "ContactInformation", id: contactId },
        { type: "ContactInformation", id: `APP_${applicationId}` },
        { type: "ContactInformation", id: "LIST" },
      ],
    }),

    // Get a single contact by ID (optional, for future use)
    getContactById: builder.query({
      query: (contactId) => ({
        url: `/contact_information/${contactId}`,
        method: "GET",
      }),
      providesTags: (result, error, contactId) => [
        { type: "ContactInformation", id: contactId },
      ],
    }),
  }),
});

export const {
  useGetContactsByApplicationQuery,
  useLazyGetContactsByApplicationQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useGetContactByIdQuery,
} = contactInformationApi;
